import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import useAnalyzing from '../utils/useAnalyzing';
import socket from '../socket';
import VerdictBar from '../components/VerdictBar';
import ShareButton from '../components/ShareButton';
import { buildReplyGroups } from '../utils/replyGroups';

const STATUS_CONFIG = {
  paywall:             { label: 'Paywalled',            color: '#92400e', bg: '#fffbeb' },
  unscrapeable:        { label: 'Inaccessible',         color: '#92400e', bg: '#fffbeb' },
  community_submitted: { label: 'Community Submitted',  color: '#1d4ed8', bg: '#eff6ff' },
  verified:            { label: 'Verified',             color: '#15803d', bg: '#f0fdf4' },
  disputed_content:    { label: 'Content Disputed',     color: '#b45309', bg: '#fff7ed' },
};

// FIX 7: unified source badge — prefers status over source_origin_type
function getSourceBadge(ec) {
  if (!ec.evidence_url) {
    return { label: 'Claimed', color: '#92400e', bg: '#fef3c7' };
  }
  if (ec.status === 'paywall') {
    return { label: 'Paywalled', color: '#92400e', bg: '#fef3c7' };
  }
  if (ec.status === 'unscrapeable' || ec.status === 'inaccessible') {
    return { label: 'Inaccessible', color: '#92400e', bg: '#fef3c7' };
  }
  switch (ec.source_origin_type) {
    case 'peer_reviewed':
    case 'primary_source':
      return { label: 'Primary Source', color: '#15803d', bg: '#f0fdf4' };
    case 'secondary_source':
      return { label: 'Secondary Source', color: '#1d4ed8', bg: '#eff6ff' };
    default:
      return { label: 'Inaccessible', color: '#92400e', bg: '#fef3c7' };
  }
}

function getDomain(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url.slice(0, 30); }
}

// Sprint A Phase 2: ReplyCard now matches IncidentDetail's ReplyMimic chrome
// (left-border + status background) so the page reads as part of the same
// debate surface. Each card links to /posts/:postId/a/:reply.id (the argument
// detail view) so the user can dig into the subtree.
const STATUS_BG = {
  green:    'var(--color-verified-bg)',
  red:      'var(--color-invalidated-bg)',
  neutral:  'var(--color-surface)',
  disputed: 'var(--color-disputed-bg, rgba(217,119,6,0.10))',
};
const STATUS_LEFT_BORDER = {
  green:    'var(--color-verified)',
  red:      'var(--color-invalidated)',
  neutral:  'var(--color-border)',
  disputed: 'var(--color-disputed, #d97706)',
};

function ReplyCard({ reply, postId }) {
  const bg = STATUS_BG[reply.status] || STATUS_BG.neutral;
  const lb = STATUS_LEFT_BORDER[reply.status] || STATUS_LEFT_BORDER.neutral;
  const inner = (
    <div style={{
      padding: '10px 14px',
      borderLeft: `3px solid ${lb}`,
      background: bg,
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-card, 8px)',
      marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 10, fontWeight: 700,
          color: reply.stance === 'affirmative' ? 'var(--color-verified)' : 'var(--color-invalidated)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          fontFamily: 'var(--font-body)',
        }}>
          {reply.stance === 'affirmative' ? 'Affirmative' : 'Rebuttal'}
        </span>
        {reply.author_username && (
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
            @{reply.author_username}
          </span>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-primary)', fontFamily: 'var(--font-body)', lineHeight: 1.5 }}>
        {reply.synopsis || reply.full_content?.slice(0, 200)}
      </p>
    </div>
  );
  if (postId && reply.id) {
    return (
      <Link to={`/posts/${postId}/a/${reply.id}`} style={{ textDecoration: 'none', display: 'block', color: 'inherit' }}>
        {inner}
      </Link>
    );
  }
  return inner;
}

function ReplyGroup({ group, postId, evidenceId }) {
  const storageKey = `evidence-group-collapsed-${evidenceId}-${group.key}`;
  const [collapsed, setCollapsed] = useState(() => {
    if (group.key === 'definitions') return false;
    try {
      const s = localStorage.getItem(storageKey);
      if (s !== null) return s === 'true';
    } catch {}
    return !group.defaultExpanded;
  });
  const toggle = () => {
    const n = !collapsed;
    setCollapsed(n);
    try { localStorage.setItem(storageKey, String(n)); } catch {}
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <button
        type="button"
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
          background: 'transparent', border: 'none',
          padding: '8px 4px',
          fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-body)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span>{group.icon}</span>
        <span>{group.title}</span>
        <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>({group.items.length})</span>
        <span style={{ marginLeft: 'auto', fontSize: 14 }}>{collapsed ? '▸' : '▾'}</span>
      </button>
      {!collapsed && group.items.map(r => <ReplyCard key={r.id} reply={r} postId={postId} />)}
    </div>
  );
}

// Map an evidence_checks row's status (+ is_challenged flag) to a VerdictBar
// verdict. is_challenged dominates because it's the structural "this evidence
// is undercut" signal that the verdict engine already honors at
// verdictEngine.js:198 — a user looking at this page should see the same
// disposition the verdict math uses. Falls back to mapping status → bucket.
function getEvidenceVerdict(ev) {
  if (!ev) return 'new';
  if (ev.is_challenged) return 'disputed';
  if (ev.status === 'verified') return 'verified';
  if (ev.status === 'disputed_content') return 'disputed';
  if (ev.status === 'paywall' || ev.status === 'unscrapeable') return 'disputed';
  if (ev.status === 'community_submitted') return 'neutral';
  return 'neutral';
}

export default function EvidencePage({ user }) {
  const { postId, evidenceId } = useParams();
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Paste prompt state
  const [pasteContent, setPasteContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);

  // Reply section state
  const [replies, setReplies] = useState([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyMsg, setReplyMsg] = useState(null);

  const fetchEvidence = useCallback(() => {
    setLoading(true);
    api.get(`/evidence/${evidenceId}`)
      .then(r => { setEvidence(r.data.evidence); setLoading(false); })
      .catch(err => { setError(err.response?.data?.error || 'Failed to load'); setLoading(false); });
  }, [evidenceId]);

  const fetchReplies = useCallback(() => {
    setRepliesLoading(true);
    api.get(`/evidence/${evidenceId}/replies`)
      .then(r => { setReplies(r.data.replies || []); setRepliesLoading(false); })
      .catch(() => setRepliesLoading(false));
  }, [evidenceId]);

  useEffect(() => {
    fetchEvidence();
    fetchReplies();
  }, [fetchEvidence, fetchReplies]);

  // Sprint A Phase 2: socket subs mirror IncidentDetail's pattern — join the
  // post room once we know the parent post id, refresh evidence + replies on
  // debate_updated/feed_updated. Pre-Phase-2 this page was static — submitting
  // a challenge required a manual refresh to see it appear in the replies
  // list. Now: cascade verdict updates and new replies surface live.
  const rootPostId = evidence?.root_post_id || null;
  const { analyzing, start: startAnalyzing, done: doneAnalyzing } = useAnalyzing(() => { fetchEvidence(); fetchReplies(); });
  useEffect(() => {
    if (!rootPostId) return;
    const postIdNum = parseInt(rootPostId, 10);
    const onDebateUpdated = ({ postId: updatedId }) => {
      if (String(updatedId) !== String(postIdNum)) return;
      doneAnalyzing();
      fetchEvidence();
      fetchReplies();
    };
    const onFeedUpdated = ({ postId: fPostId }) => {
      if (String(fPostId) !== String(postIdNum)) return;
      fetchEvidence();
      fetchReplies();
    };
    socket.on('debate_updated', onDebateUpdated);
    socket.on('feed_updated', onFeedUpdated);
    socket.emit('join_post', postIdNum);
    const onReconnect = () => {
      socket.emit('join_post', postIdNum);
      fetchEvidence();
      fetchReplies();
    };
    socket.on('connect', onReconnect);
    return () => {
      socket.emit('leave_post', postIdNum);
      socket.off('debate_updated', onDebateUpdated);
      socket.off('feed_updated', onFeedUpdated);
      socket.off('connect', onReconnect);
    };
  }, [rootPostId, fetchEvidence, fetchReplies, doneAnalyzing]);

  const handlePasteSubmit = async () => {
    if (!pasteContent.trim()) return;
    setIsSubmitting(true);
    setSubmitMsg(null);
    try {
      const r = await api.post(`/evidence/${evidenceId}/paste`, { content: pasteContent });
      setEvidence(r.data.evidence);
      setPasteContent('');
      setSubmitMsg('Content submitted — thank you.');
    } catch (err) {
      setSubmitMsg(err.response?.data?.error || 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim()) return;
    setReplySubmitting(true);
    setReplyMsg(null);
    try {
      await api.post(`/evidence/${evidenceId}/reply`, { content: replyText });
      setReplyText('');
      setReplyMsg(null);
      startAnalyzing();
      fetchReplies();
    } catch (err) {
      setReplyMsg(err.response?.data?.error || 'Failed to submit');
    } finally {
      setReplySubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ padding: 32, color: '#94a3b8', fontFamily: 'var(--font-body)', fontSize: 14 }}>
      Loading…
    </div>
  );

  if (error || !evidence) return (
    <div style={{ padding: 32, color: '#ef4444', fontFamily: 'var(--font-body)', fontSize: 14 }}>
      {error || 'Evidence not found'}
    </div>
  );

  const status = evidence.status;
  const statusCfg = STATUS_CONFIG[status] || null;
  const sourceBadge = getSourceBadge(evidence);
  const needsPaste = (status === 'paywall' || status === 'unscrapeable' || status === 'disputed_content')
    && !evidence.pasted_content;
  const isOriginalPoster = user && evidence.posted_by_user_id === user.id;
  const canPaste = (needsPaste || status === 'disputed_content') && (isOriginalPoster || !evidence.pasted_content);

  // Strip URLs already shown as the link
  const displayText = evidence.evidence_text?.replace(/https?:\/\/[^\s]+/g, '').trim();

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '24px 0' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 16px' }}>

        {/* Breadcrumb + Share */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, marginBottom: 16, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Link
              to={`/posts/${postId}`}
              style={{ fontSize: 13, color: '#64748b', textDecoration: 'none', fontFamily: 'var(--font-body)' }}
            >
              {evidence.post_title?.slice(0, 60) || 'Post'}
            </Link>
            <span style={{ color: '#cbd5e1', fontSize: 13 }}>→</span>
            <Link
              to={`/posts/${postId}/a/${evidence.argument_id}`}
              style={{ fontSize: 13, color: '#64748b', textDecoration: 'none', fontFamily: 'var(--font-body)' }}
            >
              Argument
            </Link>
            <span style={{ color: '#cbd5e1', fontSize: 13 }}>→</span>
            <span style={{ fontSize: 13, color: '#334155', fontFamily: 'var(--font-body)', fontWeight: 500 }}>Evidence</span>
          </div>
          <ShareButton
            url={typeof window !== 'undefined' ? window.location.href : ''}
            title={(evidence.evidence_text || evidence.argument_synopsis || 'Evidence').slice(0, 80)}
          />
        </div>

        {/* Context */}
        <div style={{ marginBottom: 16 }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
            letterSpacing: '0.06em', fontFamily: 'var(--font-body)', marginBottom: 4,
          }}>
            Evidence for:
          </div>
          <div style={{
            fontSize: 14, color: '#64748b', fontStyle: 'italic',
            fontFamily: 'var(--font-body)', lineHeight: 1.5,
          }}>
            {evidence.argument_synopsis || 'Argument'}
          </div>
        </div>

        {/* Main evidence card */}
        <div style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        }}>
          {/* Sprint A Phase 2: top-of-card verdict bar mirrors PostDetail +
              IncidentDetail. Maps the evidence row's status (+ is_challenged
              flag) via getEvidenceVerdict so users see the same disposition
              the verdict engine uses. */}
          <VerdictBar verdict={getEvidenceVerdict(evidence)} />
          <div style={{ padding: 20 }}>
          {/* Source badge row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
              background: sourceBadge.bg, color: sourceBadge.color,
              fontFamily: 'var(--font-body)',
            }}>
              {sourceBadge.label}
            </span>
            {statusCfg && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                background: statusCfg.bg, color: statusCfg.color, fontFamily: 'var(--font-body)',
              }}>
                {statusCfg.label}
              </span>
            )}
          </div>

          {/* URL */}
          {evidence.evidence_url && (
            <a
              href={evidence.evidence_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block', fontSize: 14, color: '#2563eb', marginBottom: 12,
                wordBreak: 'break-all', fontFamily: 'var(--font-body)',
              }}
            >
              🔗 {getDomain(evidence.evidence_url)} — {evidence.evidence_url}
            </a>
          )}

          {/* Evidence text (URL-stripped) */}
          {displayText && (
            <p style={{
              fontSize: 14, color: '#1e293b', lineHeight: 1.6,
              margin: '0 0 12px 0', fontFamily: 'var(--font-body)',
            }}>
              {displayText}
            </p>
          )}

          {/* Pasted / community content */}
          {evidence.pasted_content && (
            <div style={{
              marginTop: 12, padding: '12px 14px',
              background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7,
            }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase',
                letterSpacing: '0.05em', marginBottom: 6, fontFamily: 'var(--font-body)',
              }}>
                Community-submitted content
              </div>
              <p style={{
                fontSize: 13, color: '#1e293b', lineHeight: 1.6, margin: 0,
                fontFamily: 'var(--font-body)',
              }}>
                {evidence.pasted_content}
              </p>
            </div>
          )}
          </div>
        </div>

        {/* Paste prompt */}
        {canPaste && user && (
          <div style={{
            marginTop: 16, padding: 16, background: '#fffbeb',
            border: '1px solid #fcd34d', borderRadius: 8,
          }}>
            <p style={{ fontSize: 14, color: '#92400e', margin: '0 0 12px 0', fontFamily: 'var(--font-body)' }}>
              {status === 'disputed_content'
                ? 'The previously submitted content has been disputed. Paste the article content again to help verify what it actually says.'
                : isOriginalPoster
                  ? 'This source could not be accessed automatically. Paste the relevant content here to support your evidence.'
                  : 'This source could not be accessed. If you have access, paste the content here.'}
            </p>
            <textarea
              value={pasteContent}
              onChange={e => setPasteContent(e.target.value)}
              placeholder="Paste article content here..."
              style={{
                width: '100%', minHeight: 120, padding: 10, borderRadius: 6,
                border: '1px solid #fcd34d', fontSize: 13, fontFamily: 'inherit',
                boxSizing: 'border-box', resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
              <button
                onClick={handlePasteSubmit}
                disabled={!pasteContent.trim() || isSubmitting}
                style={{
                  padding: '8px 16px',
                  background: (pasteContent.trim() && !isSubmitting) ? '#f59e0b' : '#e5e7eb',
                  color: (pasteContent.trim() && !isSubmitting) ? '#ffffff' : '#9ca3af',
                  border: 'none', borderRadius: 6,
                  cursor: (pasteContent.trim() && !isSubmitting) ? 'pointer' : 'default',
                  fontSize: 13, fontFamily: 'inherit',
                }}
              >
                {isSubmitting ? 'Submitting…' : 'Submit Content'}
              </button>
              {submitMsg && (
                <span style={{ fontSize: 13, color: '#64748b', fontFamily: 'var(--font-body)' }}>
                  {submitMsg}
                </span>
              )}
            </div>
          </div>
        )}

        {(status === 'paywall' || status === 'unscrapeable') && !user && (
          <div style={{
            marginTop: 16, padding: 14, background: '#f8fafc',
            border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13,
            color: '#64748b', fontFamily: 'var(--font-body)',
          }}>
            <Link to="/auth" style={{ color: '#2563eb' }}>Sign in</Link> to submit the content of this source.
          </div>
        )}

        {/* Reply section */}
        <div style={{ marginTop: 24 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
            letterSpacing: '0.06em', fontFamily: 'var(--font-body)', marginBottom: 12,
          }}>
            Discussion
          </div>

          {/* Sprint A Phase 2: replies grouped by status (Why that's wrong /
              Possible reasons / Discredited) via the same buildReplyGroups
              util PostDetail uses. The /api/evidence/:id/replies endpoint
              only returns first-level evidence-challenge replies; deeper
              nesting is reached by clicking a card (each ReplyCard links to
              /posts/:postId/a/:reply.id where the subtree is rendered). */}
          {repliesLoading ? (
            <div style={{ fontSize: 13, color: '#94a3b8', fontFamily: 'var(--font-body)', marginBottom: 12 }}>
              Loading replies…
            </div>
          ) : replies.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              {buildReplyGroups(replies).map(group => (
                <ReplyGroup key={group.key} group={group} postId={postId} evidenceId={evidenceId} />
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#94a3b8', fontFamily: 'var(--font-body)', marginBottom: 16 }}>
              No discussion yet.
            </div>
          )}

          {/* Reply input */}
          {user ? (
            <div>
              <textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Challenge or discuss this evidence…"
                style={{
                  width: '100%', minHeight: 80, padding: 10, borderRadius: 6,
                  border: '1px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit',
                  boxSizing: 'border-box', resize: 'vertical', background: '#ffffff',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                <button
                  onClick={handleReplySubmit}
                  disabled={!replyText.trim() || replySubmitting}
                  style={{
                    padding: '8px 16px',
                    background: (replyText.trim() && !replySubmitting) ? 'var(--color-brand)' : '#e5e7eb',
                    color: (replyText.trim() && !replySubmitting) ? '#ffffff' : '#9ca3af',
                    border: 'none', borderRadius: 6,
                    cursor: (replyText.trim() && !replySubmitting) ? 'pointer' : 'default',
                    fontSize: 13, fontFamily: 'inherit',
                  }}
                >
                  {replySubmitting ? 'Submitting…' : 'Reply'}
                </button>
                {analyzing && (
                  <div aria-live="polite" style={{ marginTop: 8, fontSize: 13, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>
                    Analyzing your reply. It will appear here in about 20 seconds.
                  </div>
                )}
                {replyMsg && (
                  <span style={{ fontSize: 13, color: '#64748b', fontFamily: 'var(--font-body)' }}>
                    {replyMsg}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#64748b', fontFamily: 'var(--font-body)' }}>
              <Link to="/auth" style={{ color: '#2563eb' }}>Sign in</Link> to join the discussion.
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
